import React from 'react';
import { NodeResizer, useReactFlow, type NodeProps } from '@xyflow/react';

export interface CommentNodeData {
    text: string;
    [key: string]: unknown;
}

/**
 * Comment Node
 *
 * A resizable sticky note. Text was previously editable only via the
 * Inspector sidebar (Inspector.tsx has a 'comment' case) — same gap
 * Fragment and Note had. Added the same double-click-to-edit pattern used
 * there, for UX parity between the two near-identical annotation node
 * types. `NodeResizer` (for sizing) still only shows in display mode, same
 * as before.
 */
export function CommentNode({ data, selected, id }: NodeProps) {
    const nodeData = data as CommentNodeData;
    const { setNodes } = useReactFlow();
    const [isEditing, setIsEditing] = React.useState(false);
    const [tempText, setTempText] = React.useState(nodeData.text);

    const handleSaveEdit = () => {
        setNodes((nodes) =>
            nodes.map((n) => (n.id === id ? { ...n, data: { ...n.data, text: tempText } } : n))
        );
        setIsEditing(false);
    };

    if (isEditing) {
        return (
            <div
                className="nopan nodrag card shadow-lg"
                style={{ minWidth: 200, minHeight: 100 }}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="card-body p-2">
                    <textarea
                        value={tempText}
                        onChange={(e) => setTempText(e.target.value)}
                        className="form-control form-control-sm"
                        rows={3}
                        autoFocus
                    />
                    <div className="d-flex gap-2 mt-2">
                        <button
                            type="button"
                            onClick={handleSaveEdit}
                            className="btn btn-sm btn-success flex-fill"
                        >
                            Save
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                setTempText(nodeData.text);
                                setIsEditing(false);
                            }}
                            className="btn btn-sm btn-outline-secondary flex-fill"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            className="card"
            style={{
                backgroundColor: 'var(--tblr-warning)',
                opacity: 0.9,
                minWidth: 150,
                minHeight: 80,
                border: 'none',
            }}
            onDoubleClick={() => setIsEditing(true)}
        >
            {selected && <NodeResizer />}
            <div className="card-body p-2">
                <div className="small nodrag" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {nodeData.text || '(comment)'}
                </div>
            </div>
        </div>
    );
}

export const commentNodeMeta = {
    type: 'comment' as const,
    label: 'Comment',
    icon: 'ti-note',
    defaultData: { text: 'Add a note...' } satisfies CommentNodeData,
};
